import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, Cloud, CloudOff } from 'lucide-react';

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Only show "Back online" if we were previously offline
      if (wasOffline) {
        setShowReconnected(true);
        // Hide the reconnected message after 3 seconds
        setTimeout(() => {
          setShowReconnected(false);
        }, 3000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      setShowReconnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  // Don't render anything if online and not showing reconnected message
  if (isOnline && !showReconnected) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-20 md:bottom-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${
        isOnline ? 'animate-slideUp' : 'animate-slideIn'
      }`}
    >
      {!isOnline ? (
        // Offline banner
        <div className="flex items-center gap-2 px-4 py-2.5 bg-yellow-500 text-yellow-950 rounded-full shadow-lg">
          <WifiOff size={18} className="flex-shrink-0" />
          <span className="text-sm font-medium">
            You're offline — changes will sync when connected
          </span>
        </div>
      ) : (
        // Back online banner
        <div className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-full shadow-lg">
          <Cloud size={18} className="flex-shrink-0" />
          <span className="text-sm font-medium">
            Back online — syncing...
          </span>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default OfflineIndicator;